// helpers/draftStorage.ts
export type TDraftPayload = {
    version: 1;
    ts: number;
    values: Record<string, any>;
};

export const makeDraftKey = (object: string, command: string, id?: number | string) =>
    `doca:draft:${object}:${command}:${id ?? 'new'}`;

export const loadDraft = (key: string): TDraftPayload | null => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as TDraftPayload;
        // примитивная валидация версии/структуры
        if (!parsed || typeof parsed !== 'object' || typeof parsed.ts !== 'number' || !parsed.values) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

// безопасная сериализация значения поля
function serializeValue(val: any) {
    if (val instanceof Date) return val.toISOString();
    // не тащим в черновик несериализуемое
    if (typeof File !== 'undefined' && val instanceof File) return undefined;
    if (typeof Blob !== 'undefined' && val instanceof Blob) return undefined;
    if (typeof val === 'function') return undefined;

    try {
        // проверка на сериализуемость
        JSON.stringify(val);
        return val;
    } catch {
        return undefined;
    }
}

function isTrulyEmpty(val: any) {
    return val === undefined;
}

export const saveFieldToDraft = (key: string, name: string, value: any) => {
    // читаем текущий черновик
    const prev = loadDraft(key)?.values ?? {};
    const nextValues = { ...prev };

    // сериализуем «безопасно»
    const safe = serializeValue(value);

    if (isTrulyEmpty(safe)) {
        // если поле пустое — удаляем ключ из черновика
        if (name in nextValues) {
            delete nextValues[name];
        } else {
            return;
        }
    } else {
        const was = prev[name];
        const same =
            (typeof was === 'object' || typeof safe === 'object')
                ? JSON.stringify(was) === JSON.stringify(safe)
                : was === safe;

        if (same) return;
        nextValues[name] = safe;
    }

    const payload: TDraftPayload = { version: 1, ts: Date.now(), values: nextValues };

    try {
        localStorage.setItem(key, JSON.stringify(payload));
    } catch {
        // игнорируем переполнение/приватный режим
    }
};

export const clearDraft = (key: string) => {
    try {
        localStorage.removeItem(key);
    } catch {}
};

// опционально: может пригодиться для своей модалки/логики
export const getDraftDiff = (
    key: string,
    getIn: (obj: any, path: string) => any,
    currentValues: any,
    isEqual: (a: any, b: any) => boolean
): string[] => {
    const draft = loadDraft(key);
    if (!draft) return [];
    const diff: string[] = [];
    for (const k of Object.keys(draft.values)) {
        if (!isEqual(getIn(currentValues, k), draft.values[k])) diff.push(k);
    }
    return diff;
};
